import React, { useState, useEffect } from 'react';
import { LicState, UserLicense } from '../types';
import { Profession } from '../api/Professions';
import { getStateRequirementsForState } from '../api/StateRequirements';

interface LicenseFormProps {
  states: LicState[];
  professions: Profession[];
  initialData?: Partial<UserLicense>;
  onSubmit: (data: Partial<UserLicense> & { dateOfBirth?: string }) => void;
  onCancel: () => void;
  mode?: 'add' | 'edit';
  loading?: boolean;
  onDelete?: () => void;
  hideProfession?: boolean;
  userProfessionID?: string;
  /** The user's date of birth already on file, if any — skips re-asking. */
  existingDateOfBirth?: Date | string;
}

const LicenseForm: React.FC<LicenseFormProps> = ({
  states,
  professions,
  initialData = {},
  onSubmit,
  onCancel,
  mode = 'add',
  loading = false,
  onDelete,
  hideProfession = false,
  userProfessionID,
  existingDateOfBirth,
}) => {
  const getYearFromDate = (dateStr?: Date | string): string => {
    if (!dateStr) return '';
    return new Date(dateStr).getFullYear().toString();
  };

  const [form, setForm] = useState<Partial<UserLicense>>({
    professionID: initialData.professionID || userProfessionID || '',
    stateID: initialData.stateID || (states[0]?.stateID ?? 0),
    licenseNumber: initialData.licenseNumber || '',
    status: initialData.status || 'active',
  });

  const [issueYear, setIssueYear] = useState<string>(getYearFromDate(initialData.issueDate));
  const [isBirthdayAnchored, setIsBirthdayAnchored] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState<string>(
    existingDateOfBirth ? new Date(existingDateOfBirth).toISOString().slice(0, 10) : ''
  );

  // Some states (e.g. WA architects) renew on the licensee's birthday rather
  // than a fixed calendar date — those need a date of birth (and an issue
  // year) on file to compute the correct renewal cycle.
  useEffect(() => {
    let cancelled = false;
    if (!form.stateID || !form.professionID) {
      setIsBirthdayAnchored(false);
      return;
    }
    getStateRequirementsForState(Number(form.stateID))
      .then(reqs => {
        if (cancelled) return;
        const match = reqs.find(r => r.professionID === form.professionID);
        setIsBirthdayAnchored(!!match?.isBirthdayAnchored);
      })
      .catch(() => { if (!cancelled) setIsBirthdayAnchored(false); });
    return () => { cancelled = true; };
  }, [form.stateID, form.professionID]);

  const needsDOB = isBirthdayAnchored && !existingDateOfBirth;

  const handleChange = (field: keyof UserLicense, value: string | number) => {
    setForm(f => ({ ...f, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData: Partial<UserLicense> & { dateOfBirth?: string } = { ...form };
    if (issueYear && issueYear.match(/^\d{4}$/)) {
      submitData.issueDate = `${issueYear}-01-01`;
    }
    if (!submitData.status) submitData.status = 'active';
    if (needsDOB && dateOfBirth) {
      submitData.dateOfBirth = dateOfBirth;
    }
    onSubmit(submitData);
  };

  // ── Credential naming advisory ──────────────────────────────────────────
  const selectedProfession = professions.find(p => p.professionID === form.professionID);
  const activeNote = selectedProfession?.stateNotes?.find(n =>
    n.stateIDs.includes(Number(form.stateID))
  ) ?? null;
  const redirectProfession = activeNote?.redirectProfessionID
    ? professions.find(p => p.professionID === activeNote.redirectProfessionID)
    : null;

  return (
    <form onSubmit={handleSubmit}>
      {!hideProfession && (
        <div className="mb-3">
          <label className="form-label">Profession</label>
          {mode === 'add' && userProfessionID ? (
            <>
              <input
                type="text"
                className="form-control"
                value={professions.find(p => p.professionID === form.professionID)?.name || form.professionID}
                disabled
                style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
              />
              <small className="text-muted">
                Profession is set from your account profile. To add licenses for other professions, update your profile first.
              </small>
            </>
          ) : (
            <select
              className="form-select"
              value={form.professionID}
              onChange={e => handleChange('professionID', e.target.value)}
              required
            >
              <option value="">Select Profession</option>
              {professions.filter(p => p.isActive !== false).map(p => (
                <option key={p.professionID} value={p.professionID}>{p.name} ({p.professionID})</option>
              ))}
            </select>
          )}
        </div>
      )}

      <div className="mb-3">
        <label className="form-label">State</label>
        <select
          className="form-select"
          value={form.stateID}
          onChange={e => handleChange('stateID', Number(e.target.value))}
          disabled={mode === 'edit'}
          required
        >
          {states.map(s => (
            <option key={s.stateID} value={s.stateID}>{s.state}</option>
          ))}
        </select>
        {mode === 'edit' && (
          <small className="text-muted">State cannot be changed after license is created</small>
        )}
      </div>

      {/* ── Credential naming advisory ── */}
      {activeNote && mode === 'add' && (
        <div
          className="alert mb-3 d-flex gap-2"
          style={{
            background: '#fff8e1',
            border: '1px solid #f59e0b',
            borderRadius: 8,
            color: '#7c4a00',
            fontSize: '0.875rem',
          }}
          role="alert"
        >
          <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>⚠️</span>
          <div>
            <strong>Credential name mismatch</strong>
            <div className="mt-1">{activeNote.message}</div>
            {redirectProfession && (
              <button
                type="button"
                className="btn btn-sm mt-2"
                style={{
                  background: '#f59e0b',
                  color: '#fff',
                  border: 'none',
                  fontWeight: 600,
                  borderRadius: 6,
                  padding: '3px 12px',
                }}
                onClick={() => handleChange('professionID', activeNote.redirectProfessionID!)}
              >
                Switch to {redirectProfession.name} ({redirectProfession.professionID})
              </button>
            )}
          </div>
        </div>
      )}

      <div className="mb-3">
        <label className="form-label">
          {form.professionID === 'LAW' ? 'Registration Number' : 'License Number'}
        </label>
        <input
          className="form-control"
          value={form.licenseNumber}
          onChange={e => handleChange('licenseNumber', e.target.value)}
          required
        />
      </div>

      <div className="mb-3">
        <label className="form-label">Issue Year{needsDOB ? '' : ' (Optional)'}</label>
        <input
          type="text"
          className="form-control"
          placeholder="e.g., 2020"
          value={issueYear}
          onChange={e => setIssueYear(e.target.value)}
          maxLength={4}
          pattern="\d{4}"
          required={needsDOB}
        />
        <small className="text-muted">
          {needsDOB
            ? 'Required for this state — used with your date of birth below to compute your renewal cycle.'
            : 'Optional field for record keeping. Enter the 4-digit year when the license was issued (e.g., 2020).'}
        </small>
      </div>

      {needsDOB && (
        <div className="mb-3">
          <label className="form-label">Date of Birth</label>
          <input
            type="date"
            className="form-control"
            value={dateOfBirth}
            onChange={e => setDateOfBirth(e.target.value)}
            required
          />
          <small className="text-muted">
            This state renews your license on your birthday — we use this to set your renewal cycle automatically.
          </small>
        </div>
      )}

      {isBirthdayAnchored && existingDateOfBirth && (
        <div className="alert alert-light border small mb-3" role="note">
          This state renews on your birthday — we'll use the date of birth already on your profile to set your renewal cycle automatically.
        </div>
      )}

      <div className="mb-3">
        <label className="form-label">Status</label>
        <select
          className="form-select"
          value={form.status}
          onChange={e => handleChange('status', e.target.value)}
          required
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>Cancel</button>
        {mode === 'edit' && onDelete && (
          <button type="button" className="btn btn-danger me-auto" onClick={onDelete} disabled={loading}>Delete</button>
        )}
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {mode === 'edit' ? 'Save Changes' : 'Add License'}
        </button>
      </div>
    </form>
  );
};

export default LicenseForm;
